import { c as _c } from "react/compiler-runtime";
import { postHostedUserConsent } from '@onefootprint/fetch-hosted';
import type { ClickedButton, RequirementConfirmations } from '@onefootprint/request-types';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useRouter } from '@tanstack/react-router';
import { useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { getConsentContent } from '@/idv2/components/consent-footer';
import { useRequiredConsent } from '@/idv2/features/app-shell/hooks/use-required-consent';
import { type IDV2Store, StoreContext, setExternalBack, setIdv2StoreData } from '@/idv2/features/idv-to-idv2';
import { Route as RootRoute } from '@/idv2/routes/__root';
import { getLogger } from '@/idv2/utils/logger';
import { getBackQueryKey, getProceedQueryKey } from '@/idv2/utils/proceed-query';
import { trackPreviousRouteOutcome } from '@/idv2/utils/route-tracking';
const {
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'use-app-navigate'
});
const IDENTIFY_BACK_TARGETS: Record<string, string> = {
  '/identify/phone': '/identify/email',
  '/identify/kba': '/identify/login',
  '/identify/otp/sms': '/identify/phone',
  '/identify/otp/email': '/identify/email'
};
type NavigationOptions = {
  /** When selecting branch outcomes in sandbox, the successor ID to navigate to. */
  chosenSuccessorId?: string;
  /** The custom-data button the user pressed, so its node records the button's value. */
  clickedButton?: ClickedButton;
  confirmations?: RequirementConfirmations;
};
export const useAppNavigate = () => {
  const $ = _c(23);
  const navigate = useNavigate();
  const location = useLocation();
  const router = useRouter();
  const store = useContext(StoreContext);
  const queryClient = useQueryClient();
  const requiredConsent = useRequiredConsent();
  const {
    t
  } = useTranslation("idv2");
  if (!store) {
    throw new Error("useIdv2 must be used within Idv2Provider");
  }
  const {
    onClose
  } = useStore(store, useShallow(_temp));
  const {
    onboardingSessionToken
  } = RootRoute.useRouteContext();
  let t0;
  if ($[0] !== location.pathname || $[1] !== navigate || $[2] !== onboardingSessionToken || $[3] !== queryClient || $[4] !== router.latestLocation.search) {
    t0 = async (direction, options) => {
      const currentPath = location.pathname;
      if (currentPath.startsWith("/identify")) {
        if (direction === "back") {
          const backTarget = IDENTIFY_BACK_TARGETS[currentPath];
          if (backTarget) {
            return navigate({
              to: backTarget,
              search: router.latestLocation.search
            });
          }
        }
        return navigate({
          to: "/identify"
        });
      }
      if (currentPath.startsWith("/onboarding")) {
        trackPreviousRouteOutcome(direction);
        await setIdv2StoreData({
          navigation: direction
        });
        await queryClient.invalidateQueries({
          type: "all",
          queryKey: direction === "forward" ? getProceedQueryKey() : getBackQueryKey(),
          refetchType: "none"
        });
        return navigate({
          to: "/onboarding/$token",
          params: {
            token: onboardingSessionToken
          },
          search: {
            confirmations: options?.confirmations,
            chosenSuccessorId: options?.chosenSuccessorId,
            clickedButton: options?.clickedButton
          }
        });
      }
    };
    $[0] = location.pathname;
    $[1] = navigate;
    $[2] = onboardingSessionToken;
    $[3] = queryClient;
    $[4] = router.latestLocation.search;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  const navigateTo = t0;
  let t1;
  if ($[6] !== navigateTo || $[7] !== onboardingSessionToken || $[8] !== requiredConsent || $[9] !== t) {
    t1 = async args => {
      const content = getConsentContent(t, requiredConsent);
      await Promise.all(requiredConsent.map(consent => postHostedUserConsent({
        headers: {
          "X-Fp-Authorization": onboardingSessionToken
        },
        body: {
          content,
          kind: consent.kind
        }
      }).catch(error => logWarn(`Failed to record ${consent.kind} consent`, error))));
      await navigateTo("forward", args);
    };
    $[6] = navigateTo;
    $[7] = onboardingSessionToken;
    $[8] = requiredConsent;
    $[9] = t;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const forward = t1;
  let t2;
  if ($[11] !== navigateTo) {
    t2 = async () => {
      await navigateTo("back");
    };
    $[11] = navigateTo;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const back = t2;
  const currentPath_0 = location.pathname;
  let t3;
  if ($[13] !== currentPath_0) {
    t3 = currentPath_0.startsWith("/identify") && !!IDENTIFY_BACK_TARGETS[currentPath_0];
    $[13] = currentPath_0;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const identifyCanGoBack = t3;
  let t4;
  let t5;
  if ($[15] !== back) {
    t4 = () => {
      setExternalBack(back);
      return _temp2;
    };
    t5 = [back];
    $[15] = back;
    $[16] = t4;
    $[17] = t5;
  } else {
    t4 = $[16];
    t5 = $[17];
  }
  useEffect(t4, t5);
  let t6;
  if ($[18] !== back || $[19] !== forward || $[20] !== identifyCanGoBack || $[21] !== onClose) {
    t6 = {
      forward,
      back,
      close: onClose,
      identifyCanGoBack
    };
    $[18] = back;
    $[19] = forward;
    $[20] = identifyCanGoBack;
    $[21] = onClose;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
};
function _temp(state) {
  return {
    onClose: state.onClose
  };
}
function _temp2() {
  return setExternalBack(undefined);
}